import { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';

interface EntityRelationshipGraphProps {
  entityId: string;
  height?: number;
  onNodeClick?: (entity: any) => void;
}

const getAuthToken = (): string | null =>
  localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('access_token');

// Entity ontology color scheme
const NODE_COLORS: Record<string, string> = {
  VESSEL: '#3b82f6',        // blue
  AIRCRAFT: '#06b6d4',      // cyan
  PERSON: '#f97316',        // orange
  ORGANIZATION: '#a855f7',  // purple
  LOCATION: '#22c55e',      // green
  EVENT: '#ef4444',         // red
  ASSET: '#eab308',         // yellow
  DEFAULT: '#6b7280',       // gray
};

const NODE_SIZES: Record<string, number> = {
  VESSEL: 14,
  AIRCRAFT: 14,
  PERSON: 12,
  ORGANIZATION: 14,
  LOCATION: 12,
  EVENT: 12,
  ASSET: 10,
  DEFAULT: 10,
};

// Relationship type colors
const LINK_COLORS: Record<string, string> = {
  LOCATED_AT: '#22c55e',
  DEPARTS_FROM: '#3b82f6',
  ARRIVES_AT: '#3b82f6',
  TRANSITS: '#06b6d4',
  MEMBER_OF: '#a855f7',
  COMMANDS: '#f97316',
  OWNS: '#f97316',
  AFFILIATED_WITH: '#a855f7',
  FRONT_FOR: '#ef4444',
  INVOLVED_IN: '#ef4444',
  TARGET_OF: '#ef4444',
  OBSERVED_WITH: '#06b6d4',
  COMMUNICATES_WITH: '#eab308',
  ALIAS_OF: '#6b7280',
  SIMILAR_TO: '#6b7280',
  OPERATES: '#f97316',
  DEFAULT: '#4b5563',
};

interface GraphNode extends d3.SimulationNodeDatum {
  id: string;
  name: string;
  entity_type: string;
  observation_count: number;
  is_root: boolean;
  is_watchlisted?: boolean;
  entity_data?: any;
}

interface GraphLink extends d3.SimulationLinkDatum<GraphNode> {
  relationship_type: string;
  confidence: number;
  source_module?: string;
}

export function EntityRelationshipGraph({ entityId, height = 400, onNodeClick }: EntityRelationshipGraphProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const simulationRef = useRef<d3.Simulation<GraphNode, GraphLink> | null>(null);
  const [nodes, setNodes] = useState<GraphNode[]>([]);
  const [links, setLinks] = useState<GraphLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  // Fetch relationships for a given entity
  const fetchRelationships = useCallback(async (eid: string, _isRoot: boolean = false) => {
    const token = getAuthToken();
    try {
      // Fetch entity detail + relationships in parallel
      const [entityResp, relResp] = await Promise.all([
        fetch(`/api/entities/${eid}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }),
        fetch(`/api/entities/${eid}/relationships?direction=both&size=50`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }),
      ]);

      let entityData: any = null;
      if (entityResp.ok) entityData = await entityResp.json();

      let relData: any = null;
      if (relResp.ok) relData = await relResp.json();

      return { entityData, relationships: relData?.relationships || [] };
    } catch (err) {
      console.error('Failed to fetch relationships:', err);
      return { entityData: null, relationships: [] };
    }
  }, []);

  // Load initial graph
  useEffect(() => {
    if (!entityId) return;

    setLoading(true);
    setNodes([]);
    setLinks([]);

    (async () => {
      const { entityData, relationships } = await fetchRelationships(entityId, true);
      if (!entityData) {
        setLoading(false);
        return;
      }

      const nodeMap = new Map<string, GraphNode>();

      // Root node
      nodeMap.set(entityData.entity_id, {
        id: entityData.entity_id,
        name: entityData.name,
        entity_type: entityData.entity_type,
        observation_count: entityData.observation_count || 1,
        is_root: true,
        is_watchlisted: entityData.is_watchlisted,
        entity_data: entityData,
      });

      const newLinks: GraphLink[] = [];

      for (const rel of relationships) {
        const sourceId = rel.source_entity_id;
        const targetId = rel.target_entity_id;
        const otherId = sourceId === entityId ? targetId : sourceId;

        if (!nodeMap.has(otherId)) {
          // Fetch the other entity
          try {
            const token = getAuthToken();
            const resp = await fetch(`/api/entities/${otherId}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (resp.ok) {
              const otherEntity = await resp.json();
              nodeMap.set(otherId, {
                id: otherId,
                name: otherEntity.name,
                entity_type: otherEntity.entity_type,
                observation_count: otherEntity.observation_count || 1,
                is_root: false,
                is_watchlisted: otherEntity.is_watchlisted,
                entity_data: otherEntity,
              });
            }
          } catch { /* skip */ }
        }

        if (nodeMap.has(sourceId) && nodeMap.has(targetId)) {
          newLinks.push({
            source: sourceId,
            target: targetId,
            relationship_type: rel.relationship_type,
            confidence: rel.confidence || 0.5,
            source_module: rel.source_module,
          });
        }
      }

      setNodes(Array.from(nodeMap.values()));
      setLinks(newLinks);
      setLoading(false);
    })();
  }, [entityId, fetchRelationships]);

  // D3 force simulation
  useEffect(() => {
    if (!svgRef.current || !containerRef.current || nodes.length === 0) return;

    const svg = d3.select(svgRef.current);
    const width = containerRef.current.clientWidth;

    svg.selectAll('*').remove();

    const g = svg.append('g');

    // Zoom
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.3, 4])
      .on('zoom', (event) => g.attr('transform', event.transform));
    svg.call(zoom);

    // Create simulation
    const sim = d3.forceSimulation<GraphNode>(nodes)
      .force('link', d3.forceLink<GraphNode, GraphLink>(links)
        .id(d => d.id)
        .distance(80))
      .force('charge', d3.forceManyBody().strength(-200))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(20));

    simulationRef.current = sim;

    // Links
    const link = g.append('g')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', d => LINK_COLORS[d.relationship_type] || LINK_COLORS.DEFAULT)
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', d => Math.max(1, d.confidence * 3));

    // Link labels
    const linkLabel = g.append('g')
      .selectAll('text')
      .data(links)
      .join('text')
      .attr('font-size', '8px')
      .attr('fill', '#9ca3af')
      .attr('text-anchor', 'middle')
      .text(d => d.relationship_type.replace(/_/g, ' ').toLowerCase());

    // Nodes
    const node = g.append('g')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .attr('cursor', 'pointer')
      .call(d3.drag<SVGGElement, GraphNode>()
        .on('start', (event: any, d: any) => {
          if (!event.active) sim.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event: any, d: any) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event: any, d: any) => {
          if (!event.active) sim.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }) as any
      );

    // Node circles
    node.append('circle')
      .attr('r', d => {
        const base = NODE_SIZES[d.entity_type] || NODE_SIZES.DEFAULT;
        return d.is_root ? base + 4 : base + Math.min(d.observation_count, 10);
      })
      .attr('fill', d => NODE_COLORS[d.entity_type] || NODE_COLORS.DEFAULT)
      .attr('stroke', d => d.is_root ? '#fff' : d.is_watchlisted ? '#f59e0b' : 'none')
      .attr('stroke-width', d => d.is_root ? 3 : d.is_watchlisted ? 2 : 0)
      .attr('opacity', 0.9);

    // Node labels
    node.append('text')
      .attr('dy', d => {
        const base = NODE_SIZES[d.entity_type] || NODE_SIZES.DEFAULT;
        return (d.is_root ? base + 4 : base + Math.min(d.observation_count, 10)) + 12;
      })
      .attr('text-anchor', 'middle')
      .attr('font-size', d => d.is_root ? '11px' : '9px')
      .attr('font-weight', d => d.is_root ? 'bold' : 'normal')
      .attr('fill', '#e5e7eb')
      .text(d => d.name.length > 20 ? d.name.slice(0, 18) + '...' : d.name);

    // Click handler
    node.on('click', (_event, d) => {
      if (onNodeClick && d.entity_data) {
        onNodeClick(d.entity_data);
      }
    });

    // Hover effects
    node.on('mouseenter', (_event, d) => {
      setHoveredNode(d.id);
      d3.select(_event.currentTarget).select('circle').attr('opacity', 1);
    });

    node.on('mouseleave', () => {
      setHoveredNode(null);
      node.selectAll('circle').attr('opacity', 0.9);
    });

    // Simulation tick
    sim.on('tick', () => {
      link
        .attr('x1', d => (d.source as GraphNode).x!)
        .attr('y1', d => (d.source as GraphNode).y!)
        .attr('x2', d => (d.target as GraphNode).x!)
        .attr('y2', d => (d.target as GraphNode).y!);

      linkLabel
        .attr('x', d => ((d.source as GraphNode).x! + (d.target as GraphNode).x!) / 2)
        .attr('y', d => ((d.source as GraphNode).y! + (d.target as GraphNode).y!) / 2);

      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    return () => {
      sim.stop();
    };
  }, [nodes, links, height, onNodeClick]);

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="flex items-center gap-2">
          <div className="animate-spin h-4 w-4 border-2 border-cyan-400 border-t-transparent rounded-full" />
          <span className="text-xs text-muted-foreground">Loading graph...</span>
        </div>
      </div>
    );
  }

  if (nodes.length === 0) {
    return (
      <div className="flex items-center justify-center text-xs text-muted-foreground" style={{ height: height / 2 }}>
        No relationships found
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative" style={{ height }}>
      <svg
        ref={svgRef}
        width="100%"
        height={height}
        className="bg-background/50 rounded"
      />
      {/* Legend */}
      <div className="absolute bottom-2 left-2 flex flex-wrap gap-2">
        {Object.entries(NODE_COLORS).filter(([k]) => k !== 'DEFAULT').map(([type, color]) => (
          <div key={type} className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
            <span className="text-[9px] text-muted-foreground">{type}</span>
          </div>
        ))}
      </div>
      {/* Hovered node info */}
      {hoveredNode && (
        <div className="absolute top-2 right-2 bg-card border border-border rounded px-2 py-1">
          <span className="text-[10px] text-foreground">
            {nodes.find(n => n.id === hoveredNode)?.name}
          </span>
        </div>
      )}
    </div>
  );
}

export default EntityRelationshipGraph;
